import Vue from 'vue';
import { i18n } from '@/plugins/i18n';
import rootStore from '@/store';
import * as actionsTypes from './action-types';
import * as mutationsTypes from './mutation-types';
import axios from 'axios';
import { VitalityLoadingState } from '@/utils/classes/VitalityLoadingState';
import { VitalityCategory } from '@/utils/classes/VitalityCategory';

import { FETCH_TEAMS, FETCH_PB_TYPES, FETCH_ZONES } from '@/store/action-types';

const VLS = VitalityLoadingState;

export default {

  /** Fetch custom vitality groups */
  async [actionsTypes.FETCH_GROUPS](store, payload) {
    return Vue.axios({
      method: 'post',
      url: '/server/vitalityGroups/get.php',
      data: {},
    })
      .then((response) => {
        const { data } = response;
        if (data && data instanceof Array) {
          store.commit(mutationsTypes.SET_GROUPS, { data, store });
          return data;
        }
        toastr.error(i18n.t('message.CONNECTION_ERROR'));
        throw data;
      })
      .catch((error) => {
        toastr.error(i18n.t('message.CONNECTION_ERROR'));
        throw error;
      });
  },

  /** Set an array of vitality groups */
  async [actionsTypes.SET_GROUPS](store, payload) {
    return Vue.axios({
      method: 'post',
      url: '/server/vitalityGroups/set.php',
      data: payload,
    })
      .then((response) => {
        const { data } = response;
        if (data && data instanceof Array) {
          store.commit(mutationsTypes.SET_GROUPS, { data, store });
          // Presumably all groups were just newly created => Refresh the data
          setTimeout(() => {
            store.dispatch(actionsTypes.REFRESH_ALL_DATA, { onlyIfNeedsUpdate: true });
          }, 1);
          return data;
        }
        toastr.error(i18n.t('message.CONNECTION_ERROR'));
        throw data;
      })
      .catch((error) => {
        toastr.error(i18n.t('message.CONNECTION_ERROR'));
        throw error;
      });
  },

  /** Set an array of vitality groups */
  async [actionsTypes.CREATE_GROUP](store, payload) {
    return Vue.axios({
      method: 'post',
      url: '/server/vitalityGroups/create.php',
      data: payload,
    })
      .then((response) => {
        const { data } = response;
        if (data && data instanceof Array) {
          store.commit(mutationsTypes.SET_GROUPS, { data, store });
          // Refresh the data of the new group
          setTimeout(() => {
            store.dispatch(actionsTypes.REFRESH_ALL_DATA, { onlyIfNeedsUpdate: true });
          }, 1);
          return data;
        }
        toastr.error(i18n.t('message.CONNECTION_ERROR'));
        throw data;
      })
      .catch((error) => {
        toastr.error(i18n.t('message.CONNECTION_ERROR'));
        throw error;
      });
  },

  /** Edit name or teams of a v-group */
  async [actionsTypes.EDIT_GROUP](store, payload) {
    if (payload.loadingKey) {
      store.commit(mutationsTypes.SET_GROUP_LOADING, { ...payload, loadingValue: true });
    }
    return Vue.axios({
      method: 'post',
      url: '/server/vitalityGroups/edit.php',
      data: payload,
    })
      .then((response) => {
        const { data } = response;
        if (data && data instanceof Array) {
          store.commit(mutationsTypes.SET_GROUPS, { data, store });
          // Remove the loading indication
          if (payload.loadingKey) {
            store.commit(mutationsTypes.SET_GROUP_LOADING, { ...payload, loadingValue: false });
          }
          // If teams were edited, an update is probably needed
          setTimeout(() => {
            store.dispatch(actionsTypes.REFRESH_ALL_DATA, { onlyIfNeedsUpdate: true });
          }, 1);
          return data;
        }
        toastr.error(i18n.t('message.CONNECTION_ERROR'));
        throw data;
      })
      .catch((error) => {
        toastr.error(i18n.t('message.CONNECTION_ERROR'));
        throw error;
      });
  },

  /** Delete a v-group */
  async [actionsTypes.DELETE_GROUP](store, payload) {
    return Vue.axios({
      method: 'post',
      url: '/server/vitalityGroups/delete.php',
      data: payload,
    })
      .then((response) => {
        const { data } = response;
        if (data && data instanceof Array) {
          store.commit(mutationsTypes.SET_GROUPS, { data, store });
          return data;
        }
        toastr.error(i18n.t('message.CONNECTION_ERROR'));
        throw data;
      })
      .catch((error) => {
        toastr.error(i18n.t('message.CONNECTION_ERROR'));
        throw error;
      });
  },

  async [actionsTypes.REFRESH_ALL_DATA](store, payload) {
    const { onlyIfNeedsUpdate, includeInitialData } = payload;
    store.commit(mutationsTypes.SET_DATA_LOADING, { category: VitalityCategory.all, loadingState: VLS.FETCHING });
    store.commit(mutationsTypes.SET_DATA_FINALIZING, VLS.WORKING);
    store.commit(mutationsTypes.DISPLAY_FULLSCREEN_LOADING, !onlyIfNeedsUpdate);

    // I: Refresh teams, problem types and zones first if necessary
    const initialDataPromises = !includeInitialData ? [] : [
      rootStore.dispatch(FETCH_TEAMS),
      rootStore.dispatch(FETCH_ZONES),
      rootStore.dispatch(FETCH_PB_TYPES),
    ];

    Promise.all(initialDataPromises).then(() => {
      // II: FETCH_GROUPS must be called after teams are loaded
      const secondInitialPromises = !includeInitialData ? [] : [store.dispatch(actionsTypes.FETCH_GROUPS)];
      Promise.all(secondInitialPromises).then(() => {
        // III: Then fetch data for all criteria
        store.commit(mutationsTypes.SET_GROUP_LOADING, {
          ids: (onlyIfNeedsUpdate ? store.getters.updateNeededGroups : store.state.teamGroups).map(g => g.key),
          loadingKey: 'loadingData',
          loadingValue: VLS.FETCHING,
        });
        const dataPromises = VitalityCategory.all
          .map(category => store.dispatch(actionsTypes.FETCH_DATA, { ...payload, category }));
        Promise.all(dataPromises)
          .then(() => {
            store.commit(mutationsTypes.CALCULATE_ALL_GROUPS_VITALITY);
            store.commit(mutationsTypes.SET_GROUP_LOADING, {
              ids: (onlyIfNeedsUpdate ? store.getters.updateNeededGroups : store.state.teamGroups).map(g => g.key),
              loadingKey: 'loadingData',
              loadingValue: VLS.READY,
            });
            store.commit(mutationsTypes.SET_DATA_FINALIZING, VLS.READY);
          });
      });
    });
  },

  /**
   * Start fetching all necesary data to calculate teams criteria scores
   *
   * payload.category:
   *     VitalityGroup object which holds specific params and server endpoint to fetch the data
   * payload.onlyIfNeedsUpdate:
   *     true => fetch the data only for the teams in groups that need an update
   *     false => fetch data of all the teams of all groups
   */
  async [actionsTypes.FETCH_DATA](store, payload) {
    store.commit(mutationsTypes.SET_DATA_LOADING, { ...payload, loadingState: VLS.FETCHING });
    const promises = [];
    for (let i = 0, len = payload.category.dataInfo.length; i < len; i++) {
      // Override any generic post param with dataInfo item
      const params = {
        range: store.getters.rangeParam,
        teams: payload.onlyIfNeedsUpdate ? store.getters.updateNeededTeamIds : store.getters.currentGroupsTeamIds,
        ...payload.category.dataInfo[i].params,
      };
      if (params.teams.length) {
        promises.push(store.dispatch(
          actionsTypes.FETCH_SPECIFIC_DATA,
          { ...payload, ...payload.category.dataInfo[i], params },
        ));
      }
    }

    return Promise.all(promises)
      .then((data) => {
        store.commit(mutationsTypes.SET_DATA_LOADING, { ...payload, loadingState: VLS.WORKING });
        store.commit(mutationsTypes.CALCULATE_GROUP_SCORES, payload);
        store.commit(mutationsTypes.SET_DATA_LOADING, { ...payload, loadingState: VLS.READY });
        return data;
      })
      .catch((error) => {
        // Operation probably cancelled by the user
        store.commit(mutationsTypes.SET_DATA_LOADING, { ...payload, loadingState: VLS.ERROR });
        store.commit(mutationsTypes.SET_DATA_FINALIZING, false);
        throw error;
      });
  },

  /**
   * Fetch one of VitalityCategory 'dataInfo' data set
   */
  async [actionsTypes.FETCH_SPECIFIC_DATA](store, payload) {
    return Vue.axios({
      method: 'post',
      url: payload.serverUrl,
      data: payload.params,
      cancelToken: payload.cancel ? new axios.CancelToken(payload.cancel) : undefined,
    })
      .then((response) => {
        const { data } = response;
        if (data && data instanceof Array) {
          store.commit(mutationsTypes.SET_DATA, { ...payload, data });
          return data;
        }
        toastr.error(i18n.t('message.CONNECTION_ERROR'));
        throw data;
      })
      .catch((error) => {
        throw error;
      });
  },

};
